import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { RegistoSiadap } from '@/context/SiadapContext';
import type { AvaliacaoSiadap } from '@/context/SiadapContext';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useSiadap } from '@/context/SiadapContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';

interface SiadapFormModalProps {
  registo?: RegistoSiadap | null;
  funcionarioId?: string;
  modoLote?: boolean;
  onSave: (data: RegistoSiadap) => void;
  onSaveAndContinue?: (data: RegistoSiadap) => Promise<void>;
  onClose: () => void;
}

const AVALIACOES_PRE2023: AvaliacaoSiadap[] = ['Excelente', 'Relevante', 'Adequado', 'Inadequado'];
const AVALIACOES_MODERNAS: AvaliacaoSiadap[] = ['Excelente', 'Muito Bom', 'Bom', 'Regular', 'Inadequado'];

const CORES_AVALIACAO: Record<string, { bg: string; text: string; dot: string }> = {
  Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
  Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
  Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400'  },
  Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500'    },
  Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500'    },
  Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
};

function anoFinalDoPeriodo(periodo: string): number {
  const partes = periodo.trim().split('-');
  const ultimo = partes[partes.length - 1];
  const ano = parseInt(ultimo, 10);
  return isNaN(ano) ? 9999 : ano;
}

type TipoEscala = 'pre2023' | '2023_2024' | '2025plus';

function tipoEscalaDoPeriodo(periodo: string): TipoEscala {
  const anoFinal = anoFinalDoPeriodo(periodo);
  if (anoFinal < 2023) return 'pre2023';
  if (anoFinal < 2025) return '2023_2024';
  return '2025plus';
}

const ESCALA_LABELS: Record<TipoEscala, { label: string; bg: string; text: string }> = {
  pre2023:    { label: 'Escala anterior a 2023', bg: 'bg-orange-50', text: 'text-orange-700' },
  '2023_2024':{ label: 'Escala 2023–2024',       bg: 'bg-zinc-100',  text: 'text-zinc-600'  },
  '2025plus': { label: 'Escala atual (2025+)',    bg: 'bg-emerald-50',text: 'text-emerald-700'},
};

export default function SiadapFormModal({ registo, funcionarioId, modoLote, onSave, onSaveAndContinue, onClose }: SiadapFormModalProps) {
  const isEdit = !!registo;
  const { animating, handleClose } = useModalAnimation(onClose);
  const { funcionarios } = useFuncionarios();
  const { settings } = useAppSettings();
  const { registosSiadap } = useSiadap();
  const [submitting, setSubmitting] = useState(false);
  const [submittingAndContinue, setSubmittingAndContinue] = useState(false);
  const [searchFunc, setSearchFunc] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<RegistoSiadap>({
    id: 's' + String(Date.now()).slice(-6),
    funcionarioId: funcionarioId ?? '',
    periodoAvaliacao: '2023-2024',
    avaliacao: 'Bom',
    pontosChefe: 3.0,
    pontosCCA: 3.0,
    descricaoSubida: '',
    nivel: 1,
    remuneracaoAtual: 800,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof RegistoSiadap | 'avaliacao', string>>>({});

  useEffect(() => {
    if (registo) {
      setForm(registo);
    } else {
      setForm({
        id: 's' + String(Date.now()).slice(-6),
        funcionarioId: funcionarioId ?? funcionarios[0]?.id ?? '',
        periodoAvaliacao: '2023-2024',
        avaliacao: 'Bom',
        pontosChefe: 3.0,
        pontosCCA: 3.0,
        descricaoSubida: '',
        nivel: 1,
        remuneracaoAtual: 800,
      });
    }
    setErrors({});
    setSearchFunc('');
    setHighlightIdx(0);
  }, [registo, funcionarios, funcionarioId]);

  useModalOverflow();

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); };
  }, [handleClose]);

  const tipoEscala = useMemo(() => tipoEscalaDoPeriodo(form.periodoAvaliacao), [form.periodoAvaliacao]);
  const avaliacoesDisponiveis = tipoEscala === 'pre2023' ? AVALIACOES_PRE2023 : AVALIACOES_MODERNAS;

  const pontosMap = useMemo((): Record<string, number> => {
    if (tipoEscala === 'pre2023') {
      return {
        Excelente: settings.pontos_excelente_pre2023,
        Relevante: settings.pontos_relevante_pre2023,
        Adequado:  settings.pontos_adequado_pre2023,
        Inadequado:settings.pontos_inadequado_pre2023,
      };
    }
    if (tipoEscala === '2023_2024') {
      return {
        Excelente:   settings.pontos_excelente_2023,
        'Muito Bom': settings.pontos_muito_bom_2023,
        Bom:         settings.pontos_bom_2023,
        Regular:     settings.pontos_regular_2023,
        Inadequado:  settings.pontos_inadequado_2023,
      };
    }
    return {
      Excelente:   settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom:         settings.pontos_bom,
      Regular:     settings.pontos_regular,
      Inadequado:  settings.pontos_inadequado,
    };
  }, [tipoEscala, settings]);

  function handlePeriodoChange(novoPeriodo: string) {
    const novoTipo = tipoEscalaDoPeriodo(novoPeriodo);
    const novasAvaliacoes = novoTipo === 'pre2023' ? AVALIACOES_PRE2023 : AVALIACOES_MODERNAS;
    setForm((prev) => ({
      ...prev,
      periodoAvaliacao: novoPeriodo,
      avaliacao: novasAvaliacoes.includes(prev.avaliacao) ? prev.avaliacao : novasAvaliacoes[0],
    }));
    setErrors((prev) => ({ ...prev, periodoAvaliacao: undefined }));
  }

  const AVALIACOES_APENAS_PRE2023 = ['Relevante', 'Adequado'];
  const AVALIACOES_APENAS_POS2023 = ['Muito Bom', 'Regular'];

  function validate(): boolean {
    const newErrors: Partial<Record<keyof RegistoSiadap | 'avaliacao', string>> = {};
    if (!form.funcionarioId) newErrors.funcionarioId = 'Selecione um funcionário';
    if (!form.periodoAvaliacao.trim()) newErrors.periodoAvaliacao = 'Período obrigatório';
    else {
      const periodoDuplicado = registosSiadap.some(
        (r) =>
          r.funcionarioId === form.funcionarioId &&
          r.periodoAvaliacao.trim().toLowerCase() === form.periodoAvaliacao.trim().toLowerCase() &&
          r.id !== form.id
      );
      if (periodoDuplicado) {
        newErrors.periodoAvaliacao = `Já existe uma avaliação para "${form.periodoAvaliacao}" neste funcionário`;
      }
    }
    if (tipoEscala !== 'pre2023' && AVALIACOES_APENAS_PRE2023.includes(form.avaliacao)) {
      newErrors.avaliacao = `"${form.avaliacao}" é da escala anterior a 2023`;
    }
    if (tipoEscala === 'pre2023' && AVALIACOES_APENAS_POS2023.includes(form.avaliacao)) {
      newErrors.avaliacao = `"${form.avaliacao}" não pertence à escala anterior a 2023`;
    }
    if (form.pontosChefe < 1 || form.pontosChefe > 5) newErrors.pontosChefe = 'Entre 1.0 e 5.0';
    if (form.pontosCCA < 1 || form.pontosCCA > 5) newErrors.pontosCCA = 'Entre 1.0 e 5.0';
    if (form.nivel < 1) newErrors.nivel = 'Nível mínimo é 1';
    if (form.remuneracaoAtual < 0) newErrors.remuneracaoAtual = 'Valor inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit() {
    if (submitting) return;
    if (validate()) {
      setSubmitting(true);
      // O pai (page.tsx) é assíncrono; resetamos o flag quando o modal fechar
      // mas como proteção extra bloqueamos imediatamente
      onSave(form);
    }
  }

  async function handleSaveAndContinue() {
    if (submitting || submittingAndContinue) return;
    if (validate()) {
      setSubmittingAndContinue(true);
      try {
        await onSaveAndContinue?.(form);
        // Reset form para novo registo, mantém funcionário selecionado
        setForm({
          id: 's' + String(Date.now()).slice(-6),
          funcionarioId: form.funcionarioId,
          periodoAvaliacao: '2023-2024',
          avaliacao: 'Bom',
          pontosChefe: 3.0,
          pontosCCA: 3.0,
          descricaoSubida: '',
          nivel: 1,
          remuneracaoAtual: 800,
        });
        setErrors({});
        setSearchFunc('');
        setHighlightIdx(0);
        // Focus no campo de pesquisa se houver funcionário
        searchRef.current?.focus();
      } finally {
        setSubmittingAndContinue(false);
      }
    }
  }

  function set<K extends keyof RegistoSiadap>(key: K, value: RegistoSiadap[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  const escalaInfo = ESCALA_LABELS[tipoEscala];
  const funcSelecionado = funcionarios.find((f) => f.id === form.funcionarioId);

  const funcFiltrados = useMemo(() => {
    const sorted = [...funcionarios].sort((a, b) => a.nome.localeCompare(b.nome, 'pt'));
    const q = searchFunc.toLowerCase().trim();
    if (!q) return sorted;
    return sorted.filter((f) =>
      f.nome.toLowerCase().includes(q) || f.id.includes(q) || f.carreira.toLowerCase().includes(q)
    );
  }, [funcionarios, searchFunc]);

  // Reset highlight quando a lista filtra
  useEffect(() => { setHighlightIdx(0); }, [searchFunc]);

  // Scroll automático do item destacado para dentro da lista
  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll('[data-func-item]');
    const el = items[highlightIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx]);

  const handleListKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (funcFiltrados.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, funcFiltrados.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const f = funcFiltrados[highlightIdx];
      if (f) { set('funcionarioId', f.id); setSearchFunc(''); }
    }
  }, [funcFiltrados, highlightIdx]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-xl max-h-[94vh] flex flex-col overflow-hidden border border-zinc-100 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-zinc-900 flex-shrink-0">
              <i className="ri-award-line text-white text-base" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">
                {isEdit ? 'Editar' : 'Novo'} Registo
              </p>
              <h2 className="text-[15px] font-bold text-zinc-900 leading-tight">Avaliação SIADAP</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Funcionário selecionado preview */}
        {funcSelecionado && (
          <div className="px-6 py-3 border-b border-zinc-100 flex items-center gap-3 bg-white flex-shrink-0">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-zinc-900 text-white text-[11px] font-bold flex-shrink-0">
              {funcSelecionado.nome.split(' ').filter((_, i, a) => i === 0 || i === a.length - 1).map((n) => n[0]).join('')}
            </div>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-zinc-800 truncate">{funcSelecionado.nome}</p>
              <p className="text-[11px] text-zinc-400">{funcSelecionado.carreira} · <span className="font-mono">#{funcSelecionado.id}</span></p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Funcionário */}
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
              Funcionário *
            </label>

            {isEdit ? (
              /* Modo edição — só mostra o funcionário, não permite alterar */
              <div className="flex items-center gap-2.5 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-zinc-900 text-white text-[10px] font-bold flex-shrink-0">
                  {funcSelecionado?.nome.split(' ').filter((_,i,a)=>i===0||i===a.length-1).map(n=>n[0]).join('') ?? '?'}
                </div>
                <span className="text-[13px] font-medium text-zinc-700 truncate">{funcSelecionado?.nome ?? form.funcionarioId}</span>
                <span className="ml-auto text-[11px] text-zinc-400 font-mono flex-shrink-0">#{form.funcionarioId}</span>
              </div>
            ) : (
              /* Modo criação — lista compacta com scroll e pesquisa */
              <div className={`border rounded-lg overflow-hidden ${errors?.funcionarioId ? 'border-rose-300' : 'border-zinc-200'}`}>
                {/* Pesquisa com suporte a teclado */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 bg-zinc-50">
                  <i className="ri-search-line text-zinc-400 text-xs flex-shrink-0" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchFunc}
                    onChange={(e) => setSearchFunc(e.target.value)}
                    onKeyDown={handleListKeyDown}
                    placeholder="Pesquisar por nome, nº ou carreira..."
                    className="flex-1 text-[12px] bg-transparent text-zinc-700 placeholder-zinc-400 focus:outline-none"
                    autoFocus
                  />
                  {searchFunc && (
                    <button onClick={() => setSearchFunc('')} className="text-zinc-400 hover:text-zinc-600 cursor-pointer flex-shrink-0">
                      <i className="ri-close-line text-xs" />
                    </button>
                  )}
                  <span className="text-[11px] text-zinc-400 flex-shrink-0">
                    {funcFiltrados.length}/{funcionarios.length}
                  </span>
                </div>

                {/* Dica de teclado */}
                <div className="flex items-center gap-3 px-3 py-1.5 bg-zinc-50/80 border-b border-zinc-100">
                  {[['↑↓', 'navegar'], ['Enter', 'selecionar'], ['Esc', 'fechar']].map(([key, label]) => (
                    <span key={key} className="flex items-center gap-1 text-[10px] text-zinc-400">
                      <kbd className="px-1 py-0.5 bg-white border border-zinc-200 rounded text-[10px] font-mono">{key}</kbd>
                      {label}
                    </span>
                  ))}
                </div>

                {/* Lista com scroll */}
                <div ref={listRef} className="overflow-y-auto max-h-[180px]">
                  {funcFiltrados.length === 0 ? (
                    <div className="px-3 py-5 text-center">
                      <i className="ri-search-line text-zinc-300 text-lg" />
                      <p className="text-[12px] text-zinc-400 mt-1">Nenhum resultado para &ldquo;{searchFunc}&rdquo;</p>
                    </div>
                  ) : (
                    funcFiltrados.map((f, idx) => {
                      const isSelected = form.funcionarioId === f.id;
                      const isHighlighted = idx === highlightIdx;
                      const initials = f.nome.split(' ').filter((_,i,a)=>i===0||i===a.length-1).map(n=>n[0]).join('');
                      return (
                        <button
                          key={f.id}
                          data-func-item
                          type="button"
                          onClick={() => { set('funcionarioId', f.id); setSearchFunc(''); }}
                          onMouseEnter={() => setHighlightIdx(idx)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors cursor-pointer border-b border-zinc-50 last:border-0 ${
                            isSelected
                              ? 'bg-zinc-900 text-white'
                              : isHighlighted
                                ? 'bg-zinc-100 text-zinc-800'
                                : 'hover:bg-zinc-50 text-zinc-700'
                          }`}
                        >
                          <div className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 ${
                            isSelected ? 'bg-white/20 text-white' : 'bg-zinc-200 text-zinc-600'
                          }`}>
                            {initials}
                          </div>
                          <span className="text-[12px] font-medium truncate flex-1">{f.nome}</span>
                          <span className={`text-[10px] font-mono flex-shrink-0 ${
                            isSelected ? 'text-white/60' : 'text-zinc-400'
                          }`}>#{f.id}</span>
                          {isSelected
                            ? <i className="ri-check-line text-xs text-white flex-shrink-0" />
                            : isHighlighted && <i className="ri-corner-down-left-line text-xs text-zinc-400 flex-shrink-0" />
                          }
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {errors?.funcionarioId && (
              <p className="text-[11px] text-rose-500 mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />{errors?.funcionarioId}
              </p>
            )}
          </div>

          {/* Período */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest">
                Período de Avaliação *
              </label>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${escalaInfo.bg} ${escalaInfo.text}`}>
                {escalaInfo.label}
              </span>
            </div>
            <input
              type="text"
              value={form.periodoAvaliacao}
              onChange={(e) => handlePeriodoChange(e.target.value)}
              placeholder="Ex: 2023-2024 ou 2021-2022"
              className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors?.periodoAvaliacao ? 'border-rose-300 bg-rose-50/30' : 'border-zinc-200'}`}
            />
            {errors?.periodoAvaliacao && (
              <p className="text-[11px] text-rose-500 mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line text-xs" />{errors?.periodoAvaliacao}
              </p>
            )}
            <p className="text-[11px] text-zinc-400 mt-1.5">
              Formato: ano-ano (ex: 2023-2024) ou ano único (ex: 2022). A escala de avaliação muda automaticamente.
            </p>
          </div>

          {/* Avaliação */}
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
              Avaliação *
            </label>
            <div className="flex flex-wrap gap-2">
              {avaliacoesDisponiveis.map((a) => {
                const pts = pontosMap[a] ?? 0;
                const isSelected = form.avaliacao === a;
                const cfg = CORES_AVALIACAO[a] ?? CORES_AVALIACAO['Bom'];
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => set('avaliacao', a)}
                    className={`whitespace-nowrap flex items-center gap-2 px-3.5 py-2 text-[12px] font-medium rounded-xl border-2 transition-all cursor-pointer ${
                      isSelected
                        ? `${cfg.bg} ${cfg.text} border-current`
                        : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isSelected ? cfg.dot : 'bg-zinc-300'}`} />
                    {a}
                    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-lg ${isSelected ? 'bg-white/60' : 'bg-zinc-100 text-zinc-400'}`}>
                      {pts % 1 === 0 ? pts.toFixed(0) : pts.toFixed(1)} pts
                    </span>
                  </button>
                );
              })}
            </div>

            {errors?.avaliacao && (
              <div className="mt-2 flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-100 rounded-lg">
                <i className="ri-error-warning-line text-rose-500 text-sm flex-shrink-0 mt-0.5" />
                <p className="text-[11px] text-rose-600">{errors?.avaliacao}</p>
              </div>
            )}

            {/* Legenda de pontos */}
            <div className={`mt-2.5 px-3 py-2 rounded-lg text-[11px] ${escalaInfo.bg} ${escalaInfo.text}`}>
              <span className="font-semibold">{escalaInfo.label}:</span>{' '}
              {Object.entries(pontosMap).map(([k, v], i, arr) => (
                <span key={k}>{k} = <strong>{v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}</strong>{i < arr.length - 1 ? ' · ' : ''}</span>
              ))}
            </div>
          </div>

          {/* Pontuações */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Pontuações</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
                  Pontos Chefia (1–5) *
                </label>
                <div className="relative">
                  <input
                    type="number" min={1} max={5} step={0.1}
                    value={form.pontosChefe}
                    onChange={(e) => set('pontosChefe', parseFloat(e.target.value) || 1)}
                    className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors?.pontosChefe ? 'border-rose-300' : 'border-zinc-200'}`}
                  />
                </div>
                {errors?.pontosChefe && <p className="text-[11px] text-rose-500 mt-1">{errors?.pontosChefe}</p>}
                <div className="mt-1.5 h-1 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-zinc-500 rounded-full transition-all" style={{ width: `${((form.pontosChefe - 1) / 4) * 100}%` }} />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">
                  Pontos CCA (1–5) *
                </label>
                <input
                  type="number" min={1} max={5} step={0.1}
                  value={form.pontosCCA}
                  onChange={(e) => set('pontosCCA', parseFloat(e.target.value) || 1)}
                  className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors?.pontosCCA ? 'border-rose-300' : 'border-zinc-200'}`}
                />
                {errors?.pontosCCA && <p className="text-[11px] text-rose-500 mt-1">{errors?.pontosCCA}</p>}
                <div className="mt-1.5 h-1 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${((form.pontosCCA - 1) / 4) * 100}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Nível + Remuneração */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Dados Adicionais</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">Nível *</label>
                <input
                  type="number" min={1} max={50}
                  value={form.nivel}
                  onChange={(e) => set('nivel', parseInt(e.target.value, 10) || 1)}
                  className={`w-full px-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors?.nivel ? 'border-rose-300' : 'border-zinc-200'}`}
                />
                {errors?.nivel && <p className="text-[11px] text-rose-500 mt-1">{errors?.nivel}</p>}
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-1.5">Remuneração (€) *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-zinc-400 font-medium">€</span>
                  <input
                    type="number" min={0} step={0.01}
                    value={form.remuneracaoAtual}
                    onChange={(e) => set('remuneracaoAtual', parseFloat(e.target.value) || 0)}
                    className={`w-full pl-7 pr-3 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-700 focus:outline-none focus:ring-1 focus:ring-zinc-300 ${errors?.remuneracaoAtual ? 'border-rose-300' : 'border-zinc-200'}`}
                  />
                </div>
                {errors?.remuneracaoAtual && <p className="text-[11px] text-rose-500 mt-1">{errors?.remuneracaoAtual}</p>}
              </div>
            </div>
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">
              Descrição / Observações
            </label>
            <textarea
              value={form.descricaoSubida}
              onChange={(e) => set('descricaoSubida', e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Descreva os motivos da progressão ou notas relevantes..."
              className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none"
            />
            <div className="flex justify-end mt-1">
              <span className={`text-[11px] ${form.descricaoSubida.length > 450 ? 'text-amber-500' : 'text-zinc-400'}`}>
                {form.descricaoSubida.length}/500
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/40">
          <button
            type="button"
            onClick={handleClose}
            className="whitespace-nowrap px-4 py-2 text-[13px] font-medium text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <div className="flex items-center gap-2.5">
            {!isEdit && modoLote && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || submittingAndContinue}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-white text-zinc-700 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${submitting ? 'ri-loader-4-line animate-spin' : 'ri-save-line'} text-sm`} />
                </div>
                {submitting ? 'A guardar...' : 'Guardar e Fechar'}
              </button>
            )}
            {!isEdit && onSaveAndContinue && !modoLote && (
              <button
                type="button"
                onClick={handleSaveAndContinue}
                disabled={submitting || submittingAndContinue}
                className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${submittingAndContinue ? 'ri-loader-4-line animate-spin' : 'ri-add-circle-line'} text-sm`} />
                </div>
                {submittingAndContinue ? 'A guardar...' : 'Guardar e Continuar'}
              </button>
            )}
            <button
              type="button"
              onClick={modoLote && !isEdit ? handleSaveAndContinue : handleSubmit}
              disabled={submitting || submittingAndContinue}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] font-medium bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${(submitting || submittingAndContinue) ? 'ri-loader-4-line animate-spin' : isEdit ? 'ri-save-line' : modoLote ? 'ri-add-circle-line' : 'ri-add-line'} text-sm`} />
              </div>
              {submitting || submittingAndContinue ? 'A guardar...' : isEdit ? 'Guardar Alterações' : modoLote ? 'Guardar e Continuar' : 'Adicionar Registo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
